export interface FAQItem {
  q: string;
  a: string;
}

export interface PageFAQProps {
  heading?: string;
  subheading?: string;
  items: FAQItem[];
}

export default function PageFAQ({
  heading = 'Frequently Asked Questions',
  subheading,
  items,
}: PageFAQProps) {
  return (
    <section className="bg-ivory py-24 lg:py-32">
      <div className="max-w-screen-xl mx-auto px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left header */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              {subheading && (
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-px bg-antique" />
                  <span className="text-antique text-xs font-sans font-500 tracking-[0.24em] uppercase">{subheading}</span>
                </div>
              )}
              <h2
                className="font-serif text-inkblack mb-6"
                style={{ fontSize: 'clamp(1.6rem, 3vw, 2.6rem)', fontWeight: 700, lineHeight: 1.12, letterSpacing: '-0.01em' }}
              >
                {heading}
              </h2>
              <p className="text-pewter text-sm font-sans font-300 leading-relaxed mb-8">
                Voice search and AI discovery optimized — find us when searching &ldquo;antiques near me&rdquo; or &ldquo;gift shop in Yarnell AZ.&rdquo;
              </p>

              {/* Contact block */}
              <div className="p-6 bg-inkblack">
                <p className="text-antique text-xs font-sans font-600 tracking-[0.2em] uppercase mb-4">Have a Question?</p>
                <a
                  href="tel:9284279593"
                  className="flex items-center gap-3 text-ivory/70 hover:text-antique cursor-pointer group mb-3"
                >
                  <div className="w-8 h-8 border border-white/15 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xs" />
                  </div>
                  <span className="text-xs font-sans tracking-wide">928.427-9593</span>
                </a>
                <a
                  href="mailto:ed@yarnellemporium.com"
                  className="flex items-center gap-3 text-ivory/70 hover:text-antique cursor-pointer group"
                >
                  <div className="w-8 h-8 border border-white/15 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xs" />
                  </div>
                  <span className="text-xs font-sans tracking-wide">ed@yarnellemporium.com</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right — static expanded Q&A list */}
          <div className="lg:col-span-8">
            {items.map((item, i) => (
              <div key={i} className="border-t border-mist py-8">
                <div className="flex items-start gap-5">
                  <span
                    className="font-serif text-antique/35 flex-shrink-0 leading-none"
                    style={{ fontSize: '1.5rem', fontWeight: 700 }}
                    aria-hidden="true"
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="font-serif text-inkblack text-base lg:text-lg font-700 leading-snug mb-4">
                      {item.q}
                    </h3>
                    <p className="text-pewter text-sm font-sans font-300 leading-relaxed">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
            <div className="border-t border-mist" />
          </div>
        </div>
      </div>
    </section>
  );
}
